import React, {ComponentProps, useEffect, useState} from 'react';
import cn from 'classnames';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Text from '@material-ui/core/Typography';
import {makeStyles} from '@material-ui/core/styles';

import UserAvatar from 'components/pure/utils/UserAvatar';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import {CallToAction} from 'components/pure/tooltips/CallToAction';
import {DataPoint} from 'charts/types';

import Pagination from '@components/pagination';

import {ActivityGraph} from './ActivityGraph';
import {SkillBlock} from './SkillBlock';
import {ReactComponent as ColorMarker} from './color-marker.svg';
import formatSecondsShort from 'utils/formatSecondsShort';
import User from 'records/User';
import {PlanSkillDiff} from 'components/dashboard/manager/nine-box/one-rep-chart/OneRepData.utils';
import {sumBy} from 'utils/sum';
import {List} from 'immutable';
import ManagerSparklineTooltip from 'components/dashboard/admin/manager-coaching/ManagerSparklineTooltip';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import {PlanId} from 'records/CoachingPlan';

const PER_PAGE_COUNT = 5;

const GRAPH_COLOR = {
  REVIEW: '#2879FF',
  MEETING: '#E45F25',
};

interface PersonSkillsProps extends ComponentProps<typeof Grid> {
  rep: User;
  hasPlan: boolean;
  skillChanges: List<PlanSkillDiff>;
  meetingTimeData: DataPoint[];
  reviewTimeData: DataPoint[];
  from: Date;
  to: Date;
  onCreatePlan: () => void;
  isLoading: boolean;
  adminId?: string;
  managerId?: string;
  planHighlight?: PlanId;
}

export const PersonSkills: React.FC<PersonSkillsProps> = ({
  rep,
  hasPlan,
  skillChanges,
  from,
  to,
  onCreatePlan,
  isLoading,
  adminId,
  managerId,
  meetingTimeData,
  reviewTimeData,
  planHighlight,
  ...gridProps
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [page, setPage] = useState(1);
  const pagesCount = Math.ceil(skillChanges.size / PER_PAGE_COUNT);

  const reviewSum = sumBy(reviewTimeData, d => d.value);
  const meetingSum = sumBy(meetingTimeData, d => d.value);

  const chartData = [
    {color: GRAPH_COLOR.REVIEW, list: reviewTimeData},
    {color: GRAPH_COLOR.MEETING, list: meetingTimeData},
  ];

  useEffect(() => {
    const index = skillChanges.findIndex(s => s.id === planHighlight);
    if (index !== -1) {
      setPage(Math.floor(index / PER_PAGE_COUNT) + 1);
    }
  }, [planHighlight, skillChanges]);

  const {
    avatarWrap,
    graphBlock,
    graphBlockChart,
    graphBlockInfo,
    graphBlockInfoText,
    nameBlock,
    paginateBlock,
    paper,
    skillsBlock,
    skillsEmpty,
    skillsWrap,
    topBlock,
    totalHours,
    totalHoursTime,
    totalHoursLabel,
  } = useStyles();
  return (
    <Grid item {...gridProps}>
      <Paper className={cn(paper)}>
        <div className={topBlock}>
          <div className={avatarWrap}>
            <UserAvatar user={rep} />
          </div>
          <div className={nameBlock}>
            <Text component="p" variant="h4">
              {rep.firstName}
              {rep.lastName && ` ${rep.lastName[0]}.`}
            </Text>
            <Text component="p">Coaching Progress Details</Text>
            {!hasPlan && (
              <Button color="primary" variant="contained" onClick={onCreatePlan}>
                Coach Now
              </Button>
            )}
          </div>
        </div>

        <div className={graphBlock}>
          <div className={graphBlockChart}>
            <ActivityGraph from={from} to={to} data={chartData} />
          </div>
          <div className={graphBlockInfo}>
            <Text variant="h4">HOURS</Text>
            <div className={graphBlockInfoText}>
              <ColorMarker style={{color: GRAPH_COLOR.REVIEW}} />
              <Text>Review Time {formatSecondsShort(reviewSum, false, false, false)}</Text>
            </div>
            <div className={graphBlockInfoText}>
              <ColorMarker style={{color: GRAPH_COLOR.MEETING}} />
              <Text>Meeting Time {formatSecondsShort(meetingSum, false, false, false)}</Text>
            </div>
            <div className={totalHours}>
              <Text variant="h2" className={totalHoursTime}>
                {formatSecondsShort(meetingSum + reviewSum, false, false, false)}
              </Text>
              <div>
                <Text className={totalHoursLabel}>hours</Text>
                <Text className={totalHoursLabel}>total</Text>
              </div>
            </div>
            <Button color="primary" size="small" onClick={() => setShowDetails(true)}>
              View Details
            </Button>
            <Dialog open={showDetails} onClose={() => setShowDetails(false)}>
              <DialogContent>
                <ManagerSparklineTooltip
                  fromPath={''}
                  from={from}
                  to={to}
                  series={[
                    {timeType: 'Meeting', color: GRAPH_COLOR.MEETING, data: meetingTimeData},
                    {timeType: 'Review', color: GRAPH_COLOR.REVIEW, data: reviewTimeData},
                  ]}
                  meetingTotalInSeconds={meetingSum}
                  reviewTotalInSeconds={reviewSum}
                  adminId={adminId}
                  managerId={managerId}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className={skillsBlock}>
          <Text component="p" variant="h4">
            SKILL & COACHED BY
          </Text>
          <div className={skillsWrap}>
            {!skillChanges.isEmpty() || isLoading ? (
              <>
                {skillChanges
                  .slice((page - 1) * PER_PAGE_COUNT, page * PER_PAGE_COUNT)
                  .map((skill, index) => (
                    <SkillBlock
                      key={skill.id}
                      managerId={managerId}
                      adminId={adminId}
                      {...skill}
                      rep={rep}
                      index={index + (page - 1) * PER_PAGE_COUNT}
                      planHighlight={planHighlight}
                    />
                  ))}
              </>
            ) : (
              <div className={skillsEmpty}>
                <CallToAction text="There are no coaching plans in the selected date range." />
              </div>
            )}
            {isLoading && <LoadingOverlay />}
          </div>

          <div className={paginateBlock}>
            <div>
              <Pagination
                borderless
                count={pagesCount}
                page={page}
                onChange={(_, p) => setPage(p)}
              />
            </div>
          </div>
        </div>
      </Paper>
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  avatarWrap: {
    borderRadius: '50%',
    boxShadow: theme.shadows[5],
    flexBasis: '68px',
    height: 68,
    width: 68,
    padding: 5,
    '& > div': {
      height: '100%',
      width: '100%',
    },
  },
  graphBlock: {
    display: 'flex',
    flexBasis: '140px',
    flexFlow: 'row nowrap',
    width: `100%`,
  },
  graphBorder: {
    boxSizing: 'border-box',
    position: 'absolute',
    height: '100%',
    width: '100%',
    padding: '0px 0px 18px 30px',
    marginRight: -1,
  },
  graphBlockChart: {
    display: 'flex',
    flexBasis: '50%',
    justifyContent: 'flex-end',
    padding: '2px 0 10px 32px',
    position: 'relative',
    '& > section > svg': {
      borderTop: '1px solid #CCCCCC',
      borderRight: '1px solid #CCCCCC',
    },
  },
  graphBlockInfo: {
    alignItems: 'flex-start',
    display: 'flex',
    flexFlow: 'column nowrap',
    flexBasis: '50%',
    justifyContent: 'space-between',
    padding: '2px 15px 0 8px',
    width: '100%',
    '& svg': {
      marginRight: 6,
    },
  },
  graphBlockInfoText: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
  },
  nameBlock: {
    display: 'flex',
    flex: 2,
    flexFlow: 'column nowrap',
    justifyContent: 'space-around',
    paddingLeft: '20px',
    paddingTop: '4px',
    '& > p:first-child': {
      marginBottom: 6,
    },
    '& > button': {
      marginTop: 8,
      width: 'fit-content',
    },
  },
  paginateBlock: {
    alignItems: 'center',
    boxSizing: 'border-box',
    borderTop: '1px solid #D0D8E2',
    display: 'flex',
    height: 44,
    width: '100%',
    padding: '0 8px',
  },
  paper: {
    alignItems: 'center',
    display: 'flex',
    boxShadow: theme.shadows[24],
    borderRadius: 3,
    boxSizing: 'border-box',
    justifyContent: 'space-between',
    flexFlow: 'column nowrap',
    height: '100%',
    overflow: 'hidden',
    maxHeight: 666,
    maxWidth: 360,
  },
  skillsBlock: {
    marginTop: 12,
    width: '100%',
    '& > p': {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(1, 2),
      position: 'sticky',
      top: 0,
    },
  },
  skillsEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    height: '80%',
    position: 'absolute',
    width: 'calc(100% - 32px)',
    padding: '0 16px',
  },
  skillsWrap: {
    height: '260px',
    overflow: 'hidden',
    position: 'relative',
    '& > div:nth-child(even)': {
      backgroundColor: 'rgba(208, 216, 226, 0.2)',
    },
  },
  topBlock: {
    boxSizing: 'border-box',
    display: 'flex',
    flexFlow: 'row nowrap',
    width: '100%',
    padding: '20px',
  },
  totalHours: {
    alignItems: 'center',
    backgroundColor: '#F0F3F5',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'stretch',
    height: 40,
    width: '100%',
  },
  totalHoursTime: {
    alignItems: 'center',
    fontSize: 16,
    fontWeight: 700,
    display: 'flex',
    flexBasis: '60%',
    justifyContent: 'center',
  },
  totalHoursLabel: {
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    flexBasis: '40%',
    justifyContent: 'center',
  },
}));
